import {
  APP_STATE_KEY,
  CURRENT_STATE_VERSION,
  type AppState,
} from "./types";

export const buildInitialState = (): AppState => ({
  version: CURRENT_STATE_VERSION,
  stats: {},
  regionProgress: {},
  arcadeBests: {},
  settings: {
    fuzzyMatchTolerance: 2,
    showHints: true,
  },
  username: null,
  cachedLeaderboards: {},
});

const migrateState = (raw: unknown): AppState => {
  if (!raw || typeof raw !== "object") {
    return buildInitialState();
  }
  const candidate = raw as Omit<Partial<AppState>, "version"> & { version?: number };
  // v1 → v2: add username and cachedLeaderboards. v1 state otherwise
  // carries over unchanged. Anything older than v1 hard-resets.
  const v = candidate.version;
  if (v === 1 || v === CURRENT_STATE_VERSION) {
    return {
      version: CURRENT_STATE_VERSION,
      stats: candidate.stats ?? {},
      regionProgress: candidate.regionProgress ?? {},
      arcadeBests: candidate.arcadeBests ?? {},
      settings: {
        ...buildInitialState().settings,
        ...(candidate.settings ?? {}),
      },
      username: candidate.username ?? null,
      cachedLeaderboards: candidate.cachedLeaderboards ?? {},
    };
  }
  return buildInitialState();
};

export const loadState = (): AppState => {
  try {
    const serialized = localStorage.getItem(APP_STATE_KEY);
    if (!serialized) return buildInitialState();
    const parsed = JSON.parse(serialized) as unknown;
    return migrateState(parsed);
  } catch (error) {
    console.warn("loadState: failed to parse, resetting", error);
    return buildInitialState();
  }
};

// Optional hook for cloud sync. The sync module registers a callback
// here at module init; storage stays oblivious to sync's existence
// (avoids a circular import).
let onSave: ((state: AppState) => void) | null = null;
export const setSaveHook = (fn: ((state: AppState) => void) | null): void => {
  onSave = fn;
};

export const saveState = (state: AppState): void => {
  try {
    localStorage.setItem(APP_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn("saveState: localStorage write failed", error);
  }
  if (onSave) onSave(state);
};

export const resetState = (): AppState => {
  const fresh = buildInitialState();
  saveState(fresh);
  return fresh;
};
